import { useEffect, useRef, useState } from "react";
import { investigationSteps } from "../lib/data";

type Phase = "idle" | "running" | "done";

const pastInvestigations = [
  {
    id: "inv-1",
    status: "ready" as const,
    question: "Our Preethi NPS dropped 6 points in Q2. Find out why.",
    tagKind: "hero",
    conclusion: "Durability issue in the Lucknow-Gorakpur 25-34 segment. Confirmed across quant, qual, and Amazon. Traceable to one SKU and one production quarter.",
    confidence: 0.92,
    when: "2h ago",
  },
  {
    id: "inv-2",
    status: "complete" as const,
    question: "Why is the 'modern design' attribute eroding for Preethi among 35-44 owners?",
    conclusion: "Younger entrants (25-34) set a new aesthetic benchmark; the 35-44 cohort is comparing Preethi against newer brands and finding it dated. Not about product, about reference set.",
    confidence: 0.78,
    when: "yesterday",
  },
  {
    id: "inv-3",
    status: "complete" as const,
    question: "Does purchase intent actually correlate with our social listening positive-mention count?",
    conclusion: "Weakly. Intent is better predicted by survey brand imagery scores (r=0.64) than by social sentiment volume (r=0.22). Social volume is a lagging indicator, not a leading one.",
    confidence: 0.81,
    when: "3 days ago",
  },
];

export default function Investigate() {
  const [openId, setOpenId] = useState<string | null>(null);
  const [phase, setPhase] = useState<Phase>("idle");
  const [activeStep, setActiveStep] = useState(-1);
  const [completed, setCompleted] = useState(0);
  const [tab, setTab] = useState<"all" | "complete" | "running">("all");
  const [toast, setToast] = useState<string | null>(null);
  const timers = useRef<number[]>([]);

  useEffect(() => () => { timers.current.forEach(clearTimeout); }, []);

  const openHero = () => {
    setOpenId("inv-1");
    setPhase("idle");
    setActiveStep(-1);
    setCompleted(0);
  };

  const runHero = () => {
    timers.current.forEach(clearTimeout); timers.current = [];
    setPhase("running");
    setActiveStep(0);
    setCompleted(0);

    const stepDelay = 900;
    const revealDelay = 350;
    let cursor = 0;
    const schedule = () => {
      if (cursor >= investigationSteps.length) {
        timers.current.push(window.setTimeout(() => setPhase("done"), 320));
        return;
      }
      setActiveStep(cursor);
      timers.current.push(window.setTimeout(() => {
        setCompleted(cursor + 1);
        cursor += 1;
        timers.current.push(window.setTimeout(schedule, revealDelay));
      }, stepDelay));
    };
    schedule();
  };

  const showToast = (m: string) => {
    setToast(m);
    window.setTimeout(() => setToast(null), 2400);
  };

  if (openId) {
    const inv = pastInvestigations.find((x) => x.id === openId)!;
    return (
      <div className="stagger">
        <div style={{ display: "flex", alignItems: "center", gap: 14, marginBottom: 16 }}>
          <button className="btn-ghost" onClick={() => { setOpenId(null); setPhase("idle"); setCompleted(0); setActiveStep(-1); }}>
            ← All investigations
          </button>
          <span className="muted" style={{ fontSize: 12 }}>·</span>
          <span className="muted" style={{ fontSize: 12 }}>Opened {inv.when}</span>
        </div>

        <div className="thread-view">
          <div className="head">
            <div className="q">{inv.question}</div>
            <div className="meta">
              {phase === "idle" && <span className="tag teal"><span className="dot" />Ready to run</span>}
              {phase === "running" && <span className="tag teal"><span className="dot" />Running</span>}
              {phase === "done" && <span className="tag mint"><span className="dot" />Complete</span>}
              {phase !== "idle" && (
                <span className="progress">
                  <span>{completed}/{investigationSteps.length}</span>
                  <div className="progress-bar"><span style={{ width: Math.round((completed / investigationSteps.length) * 100) + "%" }} /></div>
                </span>
              )}
              {phase === "done" && <span>· 4 min 12 sec · ₹ 84</span>}
            </div>
          </div>

          {phase === "idle" ? (
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20 }}>
              <div>
                <div className="eyebrow" style={{ marginBottom: 10 }}>What Pulse will do</div>
                <ul style={{ listStyle: "none", margin: 0, padding: 0, display: "flex", flexDirection: "column", gap: 10 }}>
                  {investigationSteps.map((s, i) => (
                    <li key={i} style={{ display: "flex", alignItems: "flex-start", gap: 10, fontSize: 13.5, color: "var(--ink-60)" }}>
                      <span className="num" style={{ fontSize: 11, color: "var(--ink-40)", minWidth: 26, marginTop: 2 }}>{String(i + 1).padStart(2, "0")}</span>
                      <span><b style={{ color: "var(--ink)", fontWeight: 500 }}>{s.action}</b></span>
                    </li>
                  ))}
                </ul>
                <div style={{ marginTop: 18 }}>
                  <button className="btn-primary" onClick={runHero}>
                    Run investigation
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polygon points="5 3 19 12 5 21 5 3"/></svg>
                  </button>
                </div>
              </div>

              <div style={{ padding: "16px 18px", background: "var(--card-2)", border: "1px solid var(--line)", borderRadius: "var(--r-3)" }}>
                <div className="eyebrow" style={{ marginBottom: 10 }}>Pre-flight checks</div>
                <ul style={{ listStyle: "none", margin: 0, padding: 0, fontSize: 13.5, color: "var(--ink-2)" }}>
                  <li style={{ padding: "8px 0", borderTop: "1px solid var(--line-2)", display: "flex", gap: 10 }}><span style={{ color: "var(--ok)" }}>✓</span> Wave 1 and Wave 2 NPS variables harmonised</li>
                  <li style={{ padding: "8px 0", borderTop: "1px solid var(--line-2)", display: "flex", gap: 10 }}><span style={{ color: "var(--ok)" }}>✓</span> 18 qual transcripts indexed and searchable</li>
                  <li style={{ padding: "8px 0", borderTop: "1px solid var(--line-2)", display: "flex", gap: 10 }}><span style={{ color: "var(--ok)" }}>✓</span> Amazon India feed up to date (last pull 12 min ago)</li>
                  <li style={{ padding: "8px 0", borderTop: "1px solid var(--line-2)", display: "flex", gap: 10 }}><span style={{ color: "var(--ok)" }}>✓</span> Your budget covers this run (estimated ₹ 84)</li>
                </ul>
              </div>
            </div>
          ) : (
            <>
              <div className="steps">
                {investigationSteps.slice(0, Math.max(completed, activeStep + 1)).map((s, i) => {
                  const isDone = i < completed;
                  const isActive = i === activeStep && !isDone;
                  return (
                    <div key={i} className={"step " + (isDone ? "done" : isActive ? "active" : "")}>
                      <div className="lbl">{s.n}</div>
                      <div className="action">{s.action}</div>
                      {isDone ? (
                        <>
                          <div className="finding">{s.finding}</div>
                          {s.mini === "cityBars" && <CityBars />}
                        </>
                      ) : (
                        <div className="finding" style={{ display: "inline-flex", alignItems: "center", gap: 10 }}>
                          <span>working</span>
                          <span style={{ display: "inline-flex", gap: 3 }}>
                            <Bob /><Bob delay={0.15} /><Bob delay={0.30} />
                          </span>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>

              {phase === "done" && (
                <div className="conclusion">
                  <div className="kicker">Conclusion</div>
                  <h3>The NPS drop is <em>a durability problem</em> in the Lucknow-Gorakpur 25-34 segment, confirmed in quant, qual, and social.</h3>
                  <div className="sub">It is not a brand-love problem. It is not a price problem. It is a jar problem, and it is traceable to a specific SKU and a specific production quarter.</div>
                  <div className="chips">
                    <div className="chip">Confidence · <b>0.92</b></div>
                    <div className="chip">Sources · <b>3 of 3 agree</b></div>
                    <div className="chip">Evidence · <b>28 items</b></div>
                    <div className="chip">Handoff · <b>Product QC</b></div>
                  </div>
                  <div className="cta-row">
                    <button className="btn-primary" onClick={() => showToast("Shared to Preethi Brand Team and Product QC.")}>Share with team</button>
                    <button className="btn-secondary" onClick={() => showToast("Exported to PDF (demo only).")}>Export PDF</button>
                    <button className="btn-secondary" onClick={() => showToast("Saved to your workspace.")}>Save</button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        {phase === "done" && (
          <div className="inv-side" style={{ marginTop: 18, display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
            <div className="card">
              <h4>Suggested next actions</h4>
              <ul>
                <li><span className="k">Ops</span>Check the service-centre complaint log for jar-related RMAs, filtered to Lucknow &amp; Gorakpur, Q2.</li>
                <li><span className="k">Field</span>Book a 2-day in-market visit to 6 Preethi dealers in Lucknow; target the TV-3 jar SKU.</li>
                <li><span className="k">Brief</span>Pull Q2 production QC logs for the TV-3 jar from the vendor.</li>
                <li><span className="k">Comms</span>Draft a proactive response for the top 10 affected reviewers before this escalates.</li>
              </ul>
            </div>
            <div className="card">
              <h4>Audit trail</h4>
              <ul>
                <li><span className="k">14:22:07</span>Investigation started by CMO Preview</li>
                <li><span className="k">14:22:09</span>6 steps planned · 6 executed · 0 blocked</li>
                <li><span className="k">14:26:19</span>Conclusion generated · not over-ridden</li>
                <li><span className="k">14:26:21</span>Ready to share with team</li>
              </ul>
            </div>
          </div>
        )}

        {toast && (
          <div className="toast" role="status">
            <svg viewBox="0 0 24 24"><polyline points="20 6 9 17 4 12" /></svg>
            {toast}
          </div>
        )}
      </div>
    );
  }

  // INBOX VIEW
  const filtered = pastInvestigations;

  return (
    <div className="stagger">
      <div className="invest-header">
        <div>
          <h1 className="h-page">Investigations</h1>
          <div className="sub">Deep questions Pulse has answered for you. Click any row to re-open the full thread.</div>
        </div>
        <button className="btn-primary" onClick={() => { /* demo: no-op */ }}>
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
          Start new investigation
        </button>
      </div>

      <div className="new-inv-card">
        <div>
          <h3>The Preethi NPS drop investigation is ready for you</h3>
          <p>Pre-staged for today's walkthrough. Six steps will play out in real time - every slice, every cross-read, every source. Watch Pulse do the work your team would do in three weeks.</p>
          <div className="tip">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12" y2="16"/></svg>
            Click the top row to open it.
          </div>
        </div>
        <button className="btn-primary" onClick={openHero}>
          Open investigation
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12h14"/><path d="m13 6 6 6-6 6"/></svg>
        </button>
      </div>

      <div className="invest-tabs">
        <button className={"invest-tab " + (tab === "all" ? "active" : "")} onClick={() => setTab("all")}>All <span className="count">{pastInvestigations.length}</span></button>
        <button className={"invest-tab " + (tab === "complete" ? "active" : "")} onClick={() => setTab("complete")}>Complete <span className="count">{pastInvestigations.filter(i => i.status === "complete" || i.status === "ready").length}</span></button>
        <button className={"invest-tab " + (tab === "running" ? "active" : "")} onClick={() => setTab("running")}>Running <span className="count">0</span></button>
      </div>

      <div className="inbox">
        {filtered.map((inv, idx) => (
          <div key={inv.id} className="inbox-row" onClick={() => idx === 0 ? openHero() : null}>
            <span className={"status " + (inv.status === "ready" ? "running" : "")} />
            <div className="main">
              <div className="tag-row">
                {inv.status === "ready" && <span className="tag coral">Today's demo</span>}
                {inv.status === "complete" && <span className="tag mint">Complete</span>}
                <span className="tag">3 sources</span>
              </div>
              <div className="q">{inv.question}</div>
              <div className="concl">{inv.conclusion}</div>
            </div>
            <div className="confidence">
              <span>Confidence</span>
              <span className="val">{inv.confidence.toFixed(2)}</span>
              <div className="conf-bar"><span style={{ width: Math.round(inv.confidence * 100) + "%" }} /></div>
            </div>
            <div className="when">{inv.when}</div>
          </div>
        ))}
      </div>

      <TriangulationPanel />
    </div>
  );
}

// Signal-sync triangulation panel: quant importance × qual mention frequency × (mocked) social signal.
// Drives credibility for the meeting ask: "are quant, qual and social actually saying the same thing?"
function TriangulationPanel() {
  // Hand-curated map keyed off the actual qual themes the ETL produces and
  // the A2 importance attributes. Three signals per row: quant / qual / social.
  // "on" = signal supports the attribute; "off" = signal contradicts; "unknown" = no data.
  const rows: { label: string; quant: "on" | "off" | "unknown"; qual: "on" | "off" | "unknown"; social: "on" | "off" | "unknown"; verdict: string }[] = [
    {
      label: "Durability / sturdy build (MG)",
      quant: "on", qual: "on", social: "off",
      verdict: "divergence — quant + qual say it matters most; Amazon reviews show the opposite for one SKU",
    },
    {
      label: "Quietness across MG + Fans",
      quant: "on", qual: "on", social: "on",
      verdict: "all three signals aligned — solid common driver",
    },
    {
      label: "Energy efficiency (Fans)",
      quant: "on", qual: "unknown", social: "on",
      verdict: "quant + social aligned, qual mentions sparse — credible but worth a deeper qual probe",
    },
    {
      label: "Trust in brand (MG)",
      quant: "on", qual: "on", social: "unknown",
      verdict: "quant + qual aligned; social channels under-instrumented for this attribute",
    },
    {
      label: "Aesthetic / design (Fans)",
      quant: "on", qual: "off", social: "on",
      verdict: "divergence — quant says high importance but qual respondents talk about it sparingly",
    },
  ];

  return (
    <div className="triangulation-card">
      <h3>Signal triangulation across quant · qual · social</h3>
      <div className="muted">
        Pulse cross-checks every attribute against three independent signals. Aligned signals = confident insight; divergent signals = a question worth asking.
      </div>
      <ul className="tri-list">
        {rows.map((r) => {
          const numOn = (r.quant === "on" ? 1 : 0) + (r.qual === "on" ? 1 : 0) + (r.social === "on" ? 1 : 0);
          const anyOff = r.quant === "off" || r.qual === "off" || r.social === "off";
          const status = anyOff ? "divergence" : numOn >= 2 ? "sync" : "sync";
          return (
            <li key={r.label} className="tri-row">
              <div>
                <div className="tri-label">{r.label}</div>
                <div className="muted small" style={{ marginTop: 2 }}>{r.verdict}</div>
              </div>
              <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
                <div className="tri-signals" title="Quant · Qual · Social">
                  <span className={"tri-signal " + r.quant}  title={"Quant: " + r.quant} />
                  <span className={"tri-signal " + r.qual}   title={"Qual: " + r.qual} />
                  <span className={"tri-signal " + r.social} title={"Social: " + r.social} />
                </div>
                <span className={"tri-status " + status}>{status === "divergence" ? "DIVERGENCE" : "IN SYNC"}</span>
              </div>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

function CityBars() {
  return (
    <div className="mini">
      <svg width="260" height="60" viewBox="0 0 260 60">
        <text x="0" y="16" fontSize="11" fill="var(--ink-2)" fontFamily="JetBrains Mono">Kolkata</text>
        <rect x="90" y="8" width="150" height="10" rx="2" fill="var(--page-2)" />
        <rect x="90" y="8" width="138" height="10" rx="2" fill="var(--ok)" />
        <text x="246" y="16" fontSize="11" fill="var(--ok)" fontFamily="JetBrains Mono" fontWeight="500">+49</text>

        <text x="0" y="34" fontSize="11" fill="var(--ink-2)" fontFamily="JetBrains Mono">Lucknow</text>
        <rect x="90" y="26" width="150" height="10" rx="2" fill="var(--page-2)" />
        <rect x="90" y="26" width="72" height="10" rx="2" fill="var(--coral)" />
        <text x="246" y="34" fontSize="11" fill="var(--coral-ink)" fontFamily="JetBrains Mono" fontWeight="500">-14</text>

        <text x="0" y="52" fontSize="11" fill="var(--ink-2)" fontFamily="JetBrains Mono">Gorakpur</text>
        <rect x="90" y="44" width="150" height="10" rx="2" fill="var(--page-2)" />
        <rect x="90" y="44" width="88" height="10" rx="2" fill="var(--coral)" />
        <text x="246" y="52" fontSize="11" fill="var(--coral-ink)" fontFamily="JetBrains Mono" fontWeight="500">-11</text>
      </svg>
    </div>
  );
}

function Bob({ delay = 0 }: { delay?: number }) {
  return (
    <span style={{
      width: 6, height: 6, borderRadius: "50%", background: "var(--teal)",
      display: "inline-block",
      animation: "bob 1s ease-in-out infinite",
      animationDelay: delay + "s",
    }} />
  );
}
